
import { computed, defineComponent, onMounted, onUnmounted, ref } from 'vue'
import Article from '@/components/Article.vue'

type OrderStatus = 'pending' | 'paid_processing' | 'credited' | 'forward_failed'

interface OrderStatusResponse {
  status?: OrderStatus
  amount?: string
  error?: string
}

function queryParams(): URLSearchParams {
  const search = window.location.search.replace(/^\?/, '')
  if (search) return new URLSearchParams(search)
  const hashQuery = window.location.hash.split('?')[1] || ''
  return new URLSearchParams(hashQuery)
}

function isValidUserId(value: string): boolean {
  if (!/^\d+$/.test(value)) return false
  const numeric = Number(value)
  return Number.isSafeInteger(numeric) && numeric >= 1 && numeric <= 20000
}

function readStoredUserId (): string {
  try {
    const value = window.localStorage.getItem('mine-mcpay-userid') || ''
    return isValidUserId(value) ? value : ''
  } catch {
    return ''
  }
}

function rememberUserId (value: string): void {
  if (!isValidUserId(value)) return
  try {
    window.localStorage.setItem('mine-mcpay-userid', value)
  } catch {
    // Private browsing or disabled storage should not block payment.
  }
}

const pendingOrderKey = 'mine-mcpay-pending-order'

function isValidOrderId (value: string): boolean {
  return /^[a-f0-9]{32}$/.test(value)
}

function readPendingOrderId (): string {
  try {
    const value = window.localStorage.getItem(pendingOrderKey) || ''
    return isValidOrderId(value) ? value : ''
  } catch {
    return ''
  }
}

function rememberPendingOrderId (value: string): void {
  if (!isValidOrderId(value)) return
  try {
    window.localStorage.setItem(pendingOrderKey, value)
  } catch {
    // Payment can continue even when browser storage is unavailable.
  }
}

function forgetPendingOrderId (value: string): void {
  try {
    if (window.localStorage.getItem(pendingOrderKey) === value) {
      window.localStorage.removeItem(pendingOrderKey)
    }
  } catch {
    // Ignore unavailable browser storage.
  }
}

export default defineComponent({
  name: 'Store',
  components: { Article },
  setup () {
    const userId = ref('')
    const amount = ref('10.00')
    const isBusy = ref(false)
    const status = ref<OrderStatus | ''>('')
    const message = ref('')
    const messageClass = ref('alert-info')
    const orderId = ref('')
    let pollTimer: number | undefined

    const canPay = computed(() => isValidUserId(userId.value) && /^\d+(?:\.\d{1,2})?$/.test(amount.value) && !isBusy.value)

    function setMessage (text: string, kind: string): void {
      message.value = text
      messageClass.value = `alert-${kind}`
    }

    function showStatus (nextStatus: OrderStatus): void {
      status.value = nextStatus
      if (nextStatus === 'credited') setMessage('支付成功，充值已到账。', 'success')
      else if (nextStatus === 'forward_failed') setMessage('支付已成功，但自动充值通知失败，请联系管理员。', 'warning')
      else if (nextStatus === 'paid_processing') setMessage('支付已收到，正在处理充值。', 'info')
      else setMessage('订单尚未收到支付确认，请完成支付后等待页面更新。', 'info')
    }

    async function loadStatus (id: string): Promise<boolean> {
      try {
        const response = await fetch(`/api/orders/${encodeURIComponent(id)}/status`, { cache: 'no-store' })
        const data = await response.json() as OrderStatusResponse
        if (!response.ok || !data.status) {
          setMessage('暂时无法查询订单状态，请稍后刷新。', 'danger')
          return true
        }
        showStatus(data.status)
        const terminal = data.status === 'credited' || data.status === 'forward_failed'
        if (terminal) forgetPendingOrderId(id)
        return terminal
      } catch {
        setMessage('暂时无法连接充值服务，请稍后刷新。', 'danger')
        return true
      }
    }

    function beginPolling (id: string): void {
      const startedAt = Date.now()
      const poll = async (): Promise<void> => {
        const terminal = await loadStatus(id)
        if (terminal || Date.now() - startedAt >= 30_000) return
        pollTimer = window.setTimeout(() => { void poll() }, 2_000)
      }
      void poll()
    }

    async function startPayment (): Promise<void> {
      if (!canPay.value) {
        setMessage('请确认 UserID 在 1 至 20000 之间，并填写有效金额。', 'warning')
        return
      }

      isBusy.value = true
      setMessage('', 'info')
      try {
        const response = await fetch('/api/orders', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ userId: userId.value, amount: amount.value })
        })
        const data = await response.json() as { orderId?: string; action?: string; fields?: Record<string, string>; error?: string }
        if (!response.ok || !data.orderId || !data.action || !data.fields || !isValidOrderId(data.orderId)) {
          setMessage(data.error || '订单创建失败，请稍后重试。', 'danger')
          return
        }

        rememberPendingOrderId(data.orderId)

        const form = document.createElement('form')
        form.method = 'POST'
        form.action = data.action
        form.style.display = 'none'
        for (const [key, value] of Object.entries(data.fields)) {
          const input = document.createElement('input')
          input.type = 'hidden'
          input.name = key
          input.value = value
          form.appendChild(input)
        }
        document.body.appendChild(form)
        form.submit()
      } catch {
        setMessage('订单创建失败，请检查网络后重试。', 'danger')
      } finally {
        isBusy.value = false
      }
    }

    onMounted(() => {
      const params = queryParams()
      const queryUserId = params.get('userid') || ''
      userId.value = isValidUserId(queryUserId) ? queryUserId : readStoredUserId()
      rememberUserId(userId.value)
      const returnedOrderId = params.get('order') || ''
      orderId.value = isValidOrderId(returnedOrderId) ? returnedOrderId : readPendingOrderId()
      if (orderId.value) {
        if (params.get('payment') === 'unknown') setMessage('支付结果已返回，正在查询订单状态。', 'info')
        beginPolling(orderId.value)
      } else if (params.get('payment') === 'invalid') {
        setMessage('支付返回信息无法识别，请联系管理员确认订单，请勿重复支付。', 'warning')
      } else if (params.get('payment') === 'unknown') {
        setMessage('当前浏览器未保存对应订单，请联系管理员确认到账，请勿重复支付。', 'warning')
      }
    })

    onUnmounted(() => {
      if (pollTimer !== undefined) window.clearTimeout(pollTimer)
    })

    return { amount, canPay, isBusy, message, messageClass, startPayment, status, userId }
  }
})
